import { TemplateResult, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import globalStyles from './styles/outline-nav-item.global.css?inline';
import encapsulatedStyles from './styles/outline-nav-item.encapsulated.css?inline';
import { AdoptedStyleSheets } from '../../../controllers/adopted-stylesheets';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import {
  screenSizeMap,
} from '../../../controllers/screen-size-controller';
import { getCurrentPath, _dispatchActiveEvent, removeHangingOpen } from '../utility';
import type { MenuNavItem } from '../types';
import '../../outline-svg/outline-svg';
import '../outline-nav-link/outline-nav-link';
import '../../outline-link/outline-link';

/**
 * The element name, reused throughout the codebase
 * @type {string}
 */
const componentName = 'outline-nav-item';

/**
 * The OutlineNavItem component
 * @element outline-nav-item
 * @slot sub - for outline-nav-item/outline-nav-link
 */
@customElement('outline-nav-item')
export class OutlineNavItem extends LitElement {
  resizeObserver: ResizeObserver;

  /**
   * Adopted stylesheets for the component
   *
   * @type {AdoptedStyleSheets}
   */
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });
  private _seed = Math.floor(Math.random() * 100000).toString();
  /**
   * Item data
   */
  @property({ type: Object, attribute: 'item' })
  item: MenuNavItem;

  /**
   * Attribute used to determine if the item is a top level parent item.
   */
  @property({ type: Boolean })
  toplevel: boolean;

  /**
   * ID of parent element.
   */
  @property({ type: String, reflect: false, attribute: 'parent-id' })
  parentID: string;

  /**
   * Id used for accessibility purposes.
   */
  @property({ type: String, attribute: 'wrapper-id' })
  wrapperID = `list-item-${this._seed}`;

  /**
   * Open state of menu.
   */
  @property({ type: Boolean, attribute: 'open', reflect: true })
  open = false;

  @property({ type: Boolean })
  isMobile: boolean;

  render(): TemplateResult {
    const wrapperClasses = {
      'list-item': !this.toplevel,
      'list-item--main': this.toplevel,
      'mobile': this.isMobile,
    };

    const listClasses = {
      'list-item--sub-menu': true,
      'columns': !!(this.item.sub && this.item.sub.length > 7 && !this.isMobile),
      'mobile': this.isMobile,
    };

    return html`
      <div
        class="${classMap(wrapperClasses)}"
        id="${this.wrapperID}"
        @activeListener=${this.updateActive}
        ?engaged=${this.open}
      >
        ${(this.toplevel && !this.isMobile) ? this.desktopLinkTemplate() : this.buttonTemplate()}
        <ul
          class="${classMap(listClasses)}"
          aria-label="${this.item.text} sub menu"
          id="${this._seed}-sub-menu"
        >
          ${this.generateListItems()}
        </ul>
      </div>
    `;
  }

  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(() => {
      this.resetAtScreen();
    });    
  }
  
  firstUpdated() {
    const self = this.shadowRoot?.querySelector('.list-item--main') as HTMLElement;
    if (self) {
      this.resizeObserver.observe(self);
    }
    this.resetAtScreen();    
  }
  
  disconnectedCallback() {
    this.resizeObserver.disconnect();
  }

  resetAtScreen() {
    this.isMobile = window.innerWidth < (screenSizeMap.get('xl') as number);
  }
  /**
   * Updates the active status if activeListener event returns item.id.
   * @param e
   */
  updateActive(e: CustomEvent) {
    const childElementActive = e.detail.parentID === this.item.id;
    if (childElementActive) {
      this.setAttribute('active', 'active');
      _dispatchActiveEvent(this);
    }
  }

  /**
   * Checks if this.item.id matches current window path, emits active event if true.
   */
  checkActive() {
    if (this.item.url && getCurrentPath(this.item.url)) {
      this.setAttribute('active', 'active');
      if (this.item.parentID) {
        _dispatchActiveEvent(this);
      }
    }
  }

  /**
   * Processes this.item.sub into menu sub-components as needed.
   * @returns Menu sub-components
   */
  generateListItems() {
    if (this.item && this.item.sub) {
      return this.item.sub.map((listItem: MenuNavItem) => {
        const hasSubList = Object?.keys(listItem)?.includes('sub')
          ? true
          : false;
        if (hasSubList) {
          return html` <li>
            <outline-nav-item
              .item=${listItem}
              id=${listItem.id}
              parent-id=${this.id}
            ></outline-nav-item>
          </li>`;
        } else {
          return html`<li>
            <outline-nav-link
              id=${listItem.id}
              .item=${listItem}
              parent-id=${this.id}
            ></outline-nav-link>
          </li>`;
        }
      });
    }
    return;
  }

  /**
   * Returns the menu link
   */
  linkTemplate() {
    return html`
      <a
        class="list-item--link ${this.toplevel ? 'secondary' : ''}"
        aria-label=${ifDefined(this.item.aria)}
        href=${ifDefined(this.item.url)}
      >
        ${this.item.text}
        ${this.toplevel ? this.svgTemplate() : null}
      </a>
    `;
  }

  svgTemplate() {
    return html` 
      <outline-svg
        size="30px"
        class="list-item--button-icon-mobile"
        name="chevronRight"
        svg-fill="var(--outline-white)"
        aria-hidden="true"
      ></outline-svg>`
  }

  /**
   * Returns required span template.
   */
  spanTemplate() {
    if (this.open && this.isMobile) {
      if (this.toplevel) {
        this.parentElement?.classList.add('back');
        return html`
          <span class="list-item--label-return">Back to Main Menu</span>
        `;
      } else {
        const header = document.querySelector('outline-main-header') as HTMLElement;
        const parent = header
          .querySelector('outline-nav')
          ?.shadowRoot?.getElementById(this.parentID) as OutlineNavItem;
        const label = parent ? `to ${parent.item.text}` : '';

        return html`
          <span class="list-item--label-return">Back ${label}</span>
        `;
      }
    } else {
      return html` <span class="list-item--label"> ${this.item.text} </span> `;
    }
  }

  /**
   * Return required icon template.
   */
  iconTemplate() {
    if (this.isMobile) {
      if (this.open) {
        return html`
          <outline-svg
            size="30px"
            class="list-item--button-icon-mobile"
            name="chevronLeft"
            svg-fill="var(--outline-white)"
            aria-hidden="true"
          ></outline-svg>
        `;
      } else {
        return html`
          <outline-svg
            size="30px"
            class="list-item--button-icon-mobile"
            name="${this.open ? 'chevronDown' : 'chevronRight'}"
            svg-fill="var(--outline-white)"
            aria-hidden="true"
          ></outline-svg>
        `;
      }
    } else {
      return html` <outline-svg
        size="30px"
        class="list-item--button-icon"
        name="${this.open ? 'chevronUp' : 'chevronDown'}"
        svg-fill="var(--bjc-nav-blue)"
        aria-hidden="true"
      ></outline-svg>`;
    }
  }

  /**
   * Swaps the order of the button content depending open and mobile states.
   */
  getButtonContent() {
    return html`
      ${this.isMobile && this.open ? this.iconTemplate() : this.spanTemplate()}
      ${this.isMobile && this.open ? this.spanTemplate() : this.iconTemplate()}
    `;
  }

  buttonTemplate() {
    return html`
      <button
        class="list-item--button ${this.isMobile ? 'mobile' : null}"
        id="${this._seed}-button"
        aria-controls="${this._seed}-sub-menu"
        aria-label="${this.item.aria
          ? this.item.aria
          : `expand ${this.item.text}`} "
        aria-expanded="${this.open}"
        aria-haspopup="true"
        aria-labelledby=${this.item.url
          ? `${this._seed}-link`
          : `${this._seed}-label`}
        @click=${this.handleToggleMenu}
        @keydown=${this.handleToggleMenu}
      >
        ${this.getButtonContent()}
      </button>
    `;
  }

  desktopLinkTemplate() {
    return html `<a class="list-item--link ${this.toplevel ? 'animated' : ''}"
        aria-label=${ifDefined(this.item.aria)}
        href=${ifDefined(this.item.url)}
      >
        ${this.item.text}
        ${this.toplevel ? this.svgTemplate() : null}
      </a>
    </outline-link>
    `
  }
  /**
   * detects if component has switched between desktop and mobile sizes.
   */
  handleResize() {
    const hasSwitched =
      this.isMobile !== window.innerWidth < (screenSizeMap.get('xl') as number);
    if (hasSwitched) {
      this.isMobile = window.innerWidth < (screenSizeMap.get('xl') as number);
      this.open = false;
      this.removeMobileFlyout();
    }
  }

  /**
   * Finds all sibling elements.
   * @returns Array<HTMLElement>
   */
  getSiblingNavItems() {
    const parent = this.parentElement?.parentElement;
    let array: Array<Element> = [];
    if (parent) {
      array = [
        ...parent?.querySelectorAll('outline-nav-link, outline-nav-item'),
      ].filter(el => el.id !== this.item.id);
    }
    return array;
  }

  /**
   * Adds the 'flyaway' class to all elements returned by this.getSiblingNavItems.
   */
  applyMobileFlyout() {
    this.getSiblingNavItems().map(el =>
      el.parentElement!.classList.add('flyaway')
    );
    if (!this.toplevel) {
      const list = this.shadowRoot?.getElementById(
        `${this._seed}-sub-menu`
      ) as HTMLElement;
      const main = document.querySelector('outline-main-header') as HTMLElement;
      setTimeout(
        () =>
          main.style.setProperty(
            '--mobile-utility-offset',
            `${list.offsetHeight - 54}px`
          ),
        0
      );
    }
  }

  /**
   * Removes the 'flyaway' class to all elements returned by this.getSiblingNavItems.
   */
  removeMobileFlyout() {
    this.getSiblingNavItems().map(el =>
      el.parentElement!.classList.remove('flyaway')
    );

    if (!this.toplevel) {
      const main = document.querySelector('outline-main-header') as HTMLElement;
      setTimeout(() => main.style.removeProperty('--mobile-utility-offset'), 0);
    }
  }

  removeMobileBack() {
    this.parentElement?.classList.contains('back')
      ? this.parentElement.classList.remove('back')
      : null;
  }

  /**
   * Clears flyaway class and open attribute on child elements.
   */
  clearChildStatus() {
    const openChildren = this.shadowRoot?.querySelectorAll('[open]');
    const flyawayChildren = this.shadowRoot?.querySelectorAll('.flyaway');
    if (openChildren?.length) {
      openChildren.forEach(child => {
        const openEl = child as OutlineNavItem;
        openEl.open = false;
        openEl.clearChildStatus();
      });
    }
    if (flyawayChildren?.length) {
      flyawayChildren.forEach(child => {
        child.classList.remove('flyaway');
      });
    }
  }

  /**
   * Toggles flyout class based on this.open status.
   */
  toggleMobileFlyout() {
    if (this.open) {
      this.removeMobileFlyout();
      this.removeMobileBack();
    } else {
      this.applyMobileFlyout();
    }
  }

  /**
   * Handles flyout and open status.
   */
  toggleMenu() {
    if (this.isMobile) {
      this.toggleMobileFlyout();
    }
    if (this.open) {
      this.open = !this.open;
    } else {
      this.open = !this.open;
    }
  }

  /**
   * Toggles menu on space and Enter keyboard events.
   */
  handleToggleMenu(e: KeyboardEvent) {
    const keyed = e.type === 'keydown';
    if (keyed) {
      // make sure menu toggles only on Space and Enter keys
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleMenu();
      }
    } else {
      this.toggleMenu();
    }
  }
}

/**
 * Declare the component in the global HTMLElementTagNameMap
 */
declare global {
  interface HTMLElementTagNameMap {
    [componentName]: OutlineNavItem;
  }
}
